import React from 'react';
import { toast } from 'react-toastify';
import { ProjectLineItemDTO, ProjectLineItemsAndSummary } from '../project/project.type';
import { LineItemLabelEnum } from '../glossary/glossary.type';
import { analyzeOCRCurrencyValue, fixOCRCurrencyValue, parseCurrency, fixMisreadCurrencySymbol } from '../../common/utils/common.util';
import { CoverPageValues } from '../../context/PayappProcessContext';
import Fuse from 'fuse.js';
import { calculateBalanceToComplete, calculateRevisedBudget, calculateTotalCompletedStored } from '../project/project.util';
import { parse, format, isValid } from 'date-fns';

export const finalFieldsHeaders = [
  { field: 'description', headerName: 'Description' },
  { field: 'originalBudget', headerName: 'Original Budget' },
  { field: 'reallocation', headerName: 'Reallocation' },
  { field: 'changeOrder', headerName: 'Change Order' },
  { field: 'revisedBudget', headerName: 'Revised Budget' },
  { field: 'previousApplication', headerName: 'Previous Application' },
  { field: 'completeThisPeriod', headerName: 'Complete This Period' },
  { field: 'storedThisPeriod', headerName: 'Stored This Period' },
  { field: 'completedStoredToDate', headerName: 'Completed Stored to Date' },
  { field: 'balanceToComplete', headerName: 'Balance to Complete' },
  { field: 'retainage', headerName: 'Retainage' },
] as const;

/**
 * Header names for columns that are optional in step 2: when not assigned, we use project value
 * on merge and omit from comparison. Add new names here when extending to more columns.
 */
export const PAYAPP_OPTIONAL_COLUMN_HEADERS = ['Original Budget', 'Change Order'] as const;

/** Set of step-2 assigned column header names (e.g. Object.values(fieldHeadersMapping)). */
export type AssignedPayappColumns = Set<string>;

function hasPayappColumn(assigned: AssignedPayappColumns | undefined, headerName: string): boolean {
  return assigned != null && assigned.has(headerName);
}

export const MAX_PAYAPP_FILE_SIZE = 50 * 1024 * 1024; // 50MB
export const PAYAPP_VALUE_COLOR = '#344767';
export const PAYAPP_MISMATCH_COLOR = '#831A1A';

export type ParsedPayappRow = string[];
export type ParsedPayappTable = ParsedPayappRow[];

export const validateRows = (rowsToValidate: any[], fieldHeadersMapping: Record<string, string>): boolean => {
  const descriptionKey = Object.keys(fieldHeadersMapping).find(key => fieldHeadersMapping[key]?.toLowerCase() === 'description');

  if (!descriptionKey) {
    toast.error('Description column is missing.');
    return false;
  }

  const invalidDescriptions = rowsToValidate.filter(row => descriptionKey in row && !row[descriptionKey]?.trim());

  if (invalidDescriptions.length > 0) {
    toast.error('Some rows have an empty Description field.');
    return false;
  }

  return true;
};

export const splitIntoChunks = (arr: string[], chunkCount: number): string[][] => {
  const chunks: string[][] = [];
  const chunkSize = Math.ceil(arr.length / chunkCount);

  for (let i = 0; i < chunkCount; i++) {
    chunks.push(arr.slice(i * chunkSize, i * chunkSize + chunkSize));
  }

  return chunks;
};

// Presentation-only renames. The keys stay as-is everywhere else because they double as lookup keys
// into coverPageValues / labelToBackendFieldsMap; only the rendered text differs.
const displayLabelOverrides: Record<string, string> = {
  '%': '% Retainage',
  'Contract / Budget Sum to Date': 'Contract Sum to Date',
  'Total Completed and Stored': 'Total Completed and Stored to Date',
};

export const getDisplayLabel = (label: string) => displayLabelOverrides[label] ?? label;

export const displayFieldMapping: Record<string, string> = {
  'Original Contract Budget Sum': 'Original Budget',
  'Reallocations / Change Orders': 'Reallocation',
  'Contract / Budget Sum to Date': 'Contract / Budget Sum to Date',
  'Total Completed and Stored': 'Total Completed and Stored',
  'Total Retainage Held': 'Total Retainage Held',
  '%': '% Retainage',
  'Total Earned Less Retainage': 'Total Earned Less Retainage',
  'Less Previous Fundings': 'Less Previous Fundings',
  'Current Payment Due': 'Current Payment Due',
  'Balance to Complete Plus Retainage': 'Balance to Complete Plus Retainage',
  'Adjustments by CDCC': 'Adjustments by CDCC',
  'TOTAL AMOUNT RECOMMENDED FOR FUNDING': 'Total Amount Reccomended for Funding',
  'Requested Amount': 'Requested Amount',
  Difference: 'Difference',
  'Est. Retainage Released This Period': 'Est. Retainage Released This Period',
  'Total Contract / Budget % Complete': 'Total Contract / Budget % Complete',
};

// New flexible mapping system that allows one frontend label to map to multiple backend fields
export const labelToBackendFieldsMap: Record<string, string[]> = {
  'Application Number': ['ApplicationNumber'],
  'Period Beginning': ['PeriodFrom'],
  'Period Ending': ['PeriodTo', 'Period'],
  'Original Contract Budget Sum': ['OriginalContractBudgetSum'],
  'Reallocations / Change Orders': ['ReallocationsorChangeOrders'],
  'Contract / Budget Sum to Date': ['ContractBudgetSumtoDate'],
  'Total Completed and Stored': ['TotalCompletedandStored'],
  'Total Retainage Held': ['TotalRetainageHeld'],
  '%': ['RetainagePercentage'],
  'Total Earned Less Retainage': ['TotalEarnedLessRetainage'],
  'Less Previous Fundings': ['LessPreviousFundings'],
  'Current Payment Due': ['CurrentPaymentDue'],
  'Balance to Complete Plus Retainage': ['BalancetoCompletePlusRetainage'],
  'Adjustments by CDCC': ['AdjustmentsbyCDCC'],
  'TOTAL AMOUNT RECOMMENDED FOR FUNDING': ['TotalAmountReccomendedForFunding'],
  'Requested Amount': ['RequestedAmount', 'AmountCertified'],
  Difference: ['Difference'],
  'Est. Retainage Released This Period': ['EstRetainageReleasedThisPeriod'],
  'Total Contract / Budget % Complete': ['TotalContractBudgetPercentageComplete'],
};

// Labels whose values should not be currency-normalized (e.g. date strings like "07/11/24")
export const RAW_VALUE_LABELS = new Set(['Period Beginning', 'Period Ending']);

const PAYAPP_DATE_FORMATS = [
  'MM/dd/yy',
  'MM/dd/yyyy',
  'M/d/yy',
  'M/d/yyyy',
  'MMMM d, yyyy',
  'MMM d, yyyy',
  'MMMM dd, yyyy',
  'MMM dd, yyyy',
];

export const parsePayappDateToISO = (raw: string): string => {
  if (!raw) return raw;
  const ref = new Date();
  for (const fmt of PAYAPP_DATE_FORMATS) {
    const parsed = parse(raw.trim(), fmt, ref);
    if (isValid(parsed)) return format(parsed, 'yyyy-MM-dd');
  }
  return raw; // return original if no format matched
};

// Helper function to get the first non-null value from multiple backend fields
export const getValueFromBackendFields = (coverFields: Record<string, any>, backendFields: string[], skipNormalize = false): string => {
  for (const field of backendFields) {
    const value = coverFields[field];
    if (value !== null && value !== undefined && value !== '') {
      return skipNormalize ? (value as string) : normalizeValue(value as string);
    }
  }
  return '';
};

// Backward compatibility: create the old mapping from the new one
export const backendToLabelMap: Record<string, string> = Object.fromEntries(
  Object.entries(labelToBackendFieldsMap).flatMap(([label, fields]) => fields.map(field => [field, label])),
);

export const normalizeValue = (val: string): string => {
  if (!val) return '';

  // First, fix OCR issue where "$" is misread as a digit (e.g., "5 10,395,000.00")
  val = fixMisreadCurrencySymbol(val);

  const currencyFormatNeedsFix = analyzeOCRCurrencyValue(val, '').needsFix;
  if (currencyFormatNeedsFix) {
    val = fixOCRCurrencyValue(val);
  }

  // Check for bracketed negative like ($1,000) or (1000)
  const isBracketedNegative = /^\(\s*[-$]?[\d,.\s]+\s*\)$/.test(val);

  // Remove all non-digit, dot, minus (but keep minus only if it's not leading inside brackets)
  const cleaned = val.replace(/[^\d.-]/g, '');

  // Ensure it's negative if bracketed
  return isBracketedNegative ? `-${cleaned}` : cleaned;
};

export const primaryLabels = [
  'Original Contract Budget Sum',
  'Total Earned Less Retainage',
  'Less Previous Fundings',
  'Current Payment Due',
];

export const isPrimaryLabel = (label: string): boolean => {
  return primaryLabels.includes(label);
};

/**
 * Splits a period range string like "January 1, 2025 - January 31, 2025" into its two parts.
 * Only splits on a dash/en-dash/em-dash surrounded by spaces to avoid splitting ISO dates (2025-01-31).
 * Returns null if the string doesn't look like a range.
 */
const splitPeriodRange = (value: string): { from: string; to: string } | null => {
  // Match on space-delimited separator: " - ", " – ", " — ", or " to "
  const match = value.match(/^(.+?)\s+(?:[-–—]|to)\s+(.+)$/i);
  if (!match) return null;
  return { from: match[1].trim(), to: match[2].trim() };
};

/**
 * After the standard label-to-value mapping, fixes up period fields:
 * - If Period Ending contains a date range (from the combined "Period" field), splits it
 *   into Period Beginning and Period Ending.
 * - If Period Beginning is already set, leaves it untouched.
 */
export const postProcessPeriodCoverValues = (values: Record<string, string>): Record<string, string> => {
  const endingValue = values['Period Ending'];
  if (!endingValue) return values;

  const range = splitPeriodRange(endingValue);
  if (!range) return values; // Single date — nothing to split

  // The extracted value is a range: use the later date as Period Ending,
  // and fill Period Beginning only if it wasn't already extracted separately.
  return {
    ...values,
    'Period Ending': range.to,
    'Period Beginning': values['Period Beginning'] || range.from,
  };
};

export const coverPageFields = [
  'Application Number',
  'Period Beginning',
  'Period Ending',
  'Original Contract Budget Sum',
  'Reallocations / Change Orders',
  'Contract / Budget Sum to Date',
  'Total Completed and Stored',
  'Total Retainage Held',
  '%',
  'Total Earned Less Retainage',
  'Less Previous Fundings',
  'Current Payment Due',
  'Balance to Complete Plus Retainage',
  'Requested Amount',
];

export const allFields = [
  ...coverPageFields,
  'Adjustments by CDCC',
  'Total Amount Recommended for Funding',
  'Difference',
  'Est. Retainage Released This Period',
  'Total Contract / Budget % Complete',
];

// Fields that are calculated only - no comparison needed
export const calculatedOnlyFields = ['Total Amount Recommended for Funding', 'Difference', 'Total Contract / Budget % Complete'];

// Fields that come from payapp only - no comparison needed
export const payappOnlyFields = ['Adjustments by CDCC', 'Requested Amount'];

// Helper function to check if field should show comparison
export const shouldShowComparison = (label: string): boolean => {
  return !calculatedOnlyFields.includes(label) && !payappOnlyFields.includes(label);
};

export const mergePayappIntoProjectDraw = (
  rows: Record<string, any>[],
  draw: ProjectLineItemsAndSummary,
  coverPageValues: CoverPageValues,
  /** Set of column header names assigned in step 2 (e.g. new Set(Object.values(fieldHeadersMapping))) */
  assignedPayappColumns: AssignedPayappColumns,
) => {
  const hasOriginalBudgetColumn = hasPayappColumn(assignedPayappColumns, 'Original Budget');
  const hasChangeOrderColumn = hasPayappColumn(assignedPayappColumns, 'Change Order');
  const hasReallocationColumn = hasPayappColumn(assignedPayappColumns, 'Reallocation');
  if (!draw) {
    toast.error('An error occurred while saving the payapp. Please try again.');
    return;
  }

  // Determine if this is Application #1 (step 1)
  const applicationNumberRaw = (coverPageValues as any)?.['ApplicationNumber'] ?? (coverPageValues as any)?.['applicationNumber'] ?? '';
  const applicationNumberStr = String(applicationNumberRaw).trim();
  const isFirstApplication = applicationNumberStr === '1';

  let serialNumber = 1;
  let idCounter = 0;
  const idPrefix = `temp-${Date.now()}`;
  const payappDrawName = draw.projectDrawSummary.name;

  const drawLineItems = draw.lineItems || [];
  const existingLineItems = drawLineItems.filter(dli => !dli.isCreated) || []; //filter out any already present payapp line items

  // Check originalBudget state coming from project
  const projectOriginalBudgetValues = existingLineItems.map(li => li.originalBudget).filter(v => v !== null && v !== undefined);
  const projectHasAnyOriginalBudget = projectOriginalBudgetValues.length > 0;
  const projectOriginalBudgetAllZero = projectHasAnyOriginalBudget && projectOriginalBudgetValues.every(v => Number(v) === 0);
  const projectOriginalBudgetAllZeroOrMissing = !projectHasAnyOriginalBudget || projectOriginalBudgetAllZero;

  const updatedLineItems: ProjectLineItemDTO[] = [];
  const matchedProjectItemIds = new Set<string>();

  const matchedLineItemIds = new Set<string>();

  rows.forEach((row: Record<string, any>, payappIndex) => {
    const normalizedDesc = (row.description || '').trim().toLowerCase();
    const rowOriginalBudget = parseCurrency(row.originalBudget);

    // Step 1: Try exact matching first (description + originalBudget, and not already matched)
    let exactMatchIndex = existingLineItems.findIndex(
      (existing, index) =>
        existing.lineItem.description?.trim().toLowerCase() === normalizedDesc &&
        parseFloat(existing.originalBudget.toString()) === rowOriginalBudget &&
        !matchedProjectItemIds.has(existing.id),
    );

    // Step 2: If no exact budget match, fallback to first unmatched description match
    if (exactMatchIndex === -1) {
      exactMatchIndex = existingLineItems.findIndex(
        (existing, index) =>
          existing.lineItem.description?.trim().toLowerCase() === normalizedDesc && !matchedProjectItemIds.has(existing.id),
      );
    }

    // Create PayApp line item representation
    const revisedBudgetRaw = row.revisedBudget;
    const useRevised = revisedBudgetRaw !== undefined && revisedBudgetRaw !== null && revisedBudgetRaw !== '';
    const incomingRevisedBudget = useRevised ? parseCurrency(revisedBudgetRaw) : 0;
    const newOriginalBudget = parseCurrency(row.originalBudget) || 0;

    if (exactMatchIndex !== -1) {
      // Exact match found
      const existing = existingLineItems[exactMatchIndex];
      matchedProjectItemIds.add(existing.id);

      const changeOrderValue =
        hasChangeOrderColumn === true && row.changeOrder !== undefined && row.changeOrder !== null && row.changeOrder !== ''
          ? parseCurrency(row.changeOrder)
          : (existing.changeOrder ?? 0);

      const calculatedReallocation = useRevised
        ? incomingRevisedBudget - (existing.originalBudget + changeOrderValue)
        : parseCurrency(row.reallocation);

      // Determine which division to use - prefer PayApp division if available
      const divisionToUse = row.division || existing.lineItem.division;

      const calculatedPreviousApplication = existing.previousApplication + existing.completeThisPeriod + existing.storedThisPeriod;
      const calculatedCompletedStoredToDate =
        calculatedPreviousApplication + (existing.completeThisPeriod || 0) + (existing.storedThisPeriod || 0);
      const calculatedBalanceToComplete = incomingRevisedBudget - calculatedCompletedStoredToDate;

      // Store values for later comparison in step 3
      // Omit originalBudget when payapp had no OB column so discrepancy is not flagged (consistent with handleMergeFuzzyMatch / autoMergeFuzzyMatch)
      const payappValues = {
        originalBudget: hasOriginalBudgetColumn && row?.originalBudget ? parseCurrency(row?.originalBudget) : undefined,
        reallocation: row?.reallocation ? parseCurrency(row?.reallocation) : undefined,
        changeOrder: hasChangeOrderColumn && row?.changeOrder ? parseCurrency(row?.changeOrder) : undefined,
        revisedBudget: row?.revisedBudget ? parseCurrency(row?.revisedBudget) : undefined,
        previousApplication: row?.previousApplication ? parseCurrency(row?.previousApplication) : undefined,
        completeThisPeriod: row?.completeThisPeriod ? parseCurrency(row?.completeThisPeriod) : undefined,
        storedThisPeriod: row?.storedThisPeriod ? parseCurrency(row?.storedThisPeriod) : undefined,
        completedStoredToDate: row?.completedStoredToDate ? parseCurrency(row?.completedStoredToDate) : undefined,
        balanceToComplete: row?.balanceToComplete ? parseCurrency(row?.balanceToComplete) : undefined,
        retainage: row?.retainage ? parseCurrency(row?.retainage) : undefined,
      };

      const projectValues = {
        originalBudget: existing.originalBudget,
        reallocation: hasReallocationColumn ? existing.reallocation : undefined,
        changeOrder: existing.changeOrder,
        revisedBudget: calculateRevisedBudget(existing),
        previousApplication: existing.previousApplication,
        completeThisPeriod: existing.completeThisPeriod,
        storedThisPeriod: existing.storedThisPeriod,
        completedStoredToDate: calculateTotalCompletedStored(existing),
        balanceToComplete: calculateBalanceToComplete(existing),
        retainage: existing.retainage,
      };

      const updated: ProjectLineItemDTO = {
        ...existing,
        previousApplication: (existing.previousApplication || 0) + (existing.completeThisPeriod || 0) + (existing.storedThisPeriod || 0),
        completeThisPeriod: parseCurrency(row.completeThisPeriod),
        storedThisPeriod: parseCurrency(row.storedThisPeriod),
        cdccApproved: (parseCurrency(row.completeThisPeriod) || 0) + (parseCurrency(row.storedThisPeriod) || 0),
        changeOrder: changeOrderValue,
        reallocation: calculatedReallocation,
        quantity: parseFloat(row.quantity) || 0,
        retainage:
          row.retainage !== undefined && row.retainage !== null && row.retainage !== ''
            ? parseCurrency(row.retainage)
            : (existing.retainage ?? 0),
        // Update the lineItem with the preferred division
        lineItem: {
          ...existing.lineItem,
          division: divisionToUse,
        },
        serialNumber: serialNumber++,
        isEdited: true,
        isCreated: false,
        isDeleted: false,
        matchType: 'exact',
        payappRow: payappValues,
        projectRow: projectValues,
        labels: row.isChangeOrder
          ? [...(existing.labels || []).filter(l => l !== LineItemLabelEnum.ChangeOrder), LineItemLabelEnum.ChangeOrder]
          : existing.labels,
        __payappSourceId: row.__payappSourceId,
      };

      if (!hasPayappMismatch(updated) && !hasProjectMismatch(updated)) {
        delete updated.payappRow;
        delete updated.projectRow;
      }

      updatedLineItems.push(updated);
    } else {
      // No exact match found - try fuzzy matching or treat as PayApp-only
      const unmatchedProjectItems = existingLineItems.filter(item => !matchedProjectItemIds.has(item.id));

      const incomingCO = parseCurrency(row.changeOrder);
      const incomingReallocation = parseCurrency(row.reallocation);

      // Try fuzzy matching
      const payappDescription = (row.description || '').trim().toLowerCase() || '';
      const payappRevisedBudget = incomingRevisedBudget || 0;

      // Find potential fuzzy matches
      const potentialMatches: Array<{ item: ProjectLineItemDTO; score: number; budgetMatch: boolean }> = [];

      unmatchedProjectItems.forEach(projectItem => {
        const projectDescription = projectItem.lineItem.description?.trim().toLowerCase() || '';
        const projectRevisedBudget = (projectItem.originalBudget || 0) + (projectItem.reallocation || 0) + (projectItem.changeOrder || 0);

        const fuzzyScore = fuzzyMatchDescription(payappDescription, projectDescription);
        const budgetMatch = budgetWithinTolerance(payappRevisedBudget, projectRevisedBudget);

        // Consider it a potential match if fuzzy score is above threshold
        if (fuzzyScore >= FUZZY_MATCH_THRESHOLD) {
          potentialMatches.push({
            item: projectItem,
            score: fuzzyScore,
            budgetMatch,
          });
        }
      });

      // Sort potential matches by score (best first), then by budget match
      potentialMatches.sort((a, b) => {
        if (a.budgetMatch && !b.budgetMatch) return -1;
        if (!a.budgetMatch && b.budgetMatch) return 1;
        return b.score - a.score;
      });

      // Take the best match if any
      const bestMatch = potentialMatches[0];

      // Decide how to split Revised Budget among Original Budget and Reallocation
      let effectiveOriginalBudget = newOriginalBudget;
      let calculatedReallocation = incomingReallocation;

      if (useRevised) {
        if (!bestMatch && !hasOriginalBudgetColumn && (isFirstApplication || projectOriginalBudgetAllZeroOrMissing)) {
          // PayApp-only line item, no OB column in step 2, and either:
          // - Application Number is 1, or
          // - Project has no / zero original budgets
          //
          // OB = Revised - (Reallocation + CO)
          effectiveOriginalBudget = incomingRevisedBudget - (incomingReallocation + incomingCO);
          calculatedReallocation = incomingReallocation;
        } else {
          // Existing (or fuzzy-matched) project item OR Original Budget was mapped OR this is a later app
          // Keep existing behavior – put discrepancy into Reallocation.
          calculatedReallocation = incomingRevisedBudget - (newOriginalBudget + incomingCO);
        }
      }

      // Create PayApp line item for fuzzy matching / payapp-only
      const payappLineItem: ProjectLineItemDTO = {
        id: `${idPrefix}-payapp-${++idCounter}`,
        lineItem: {
          id: '',
          description: row.description || '',
          division: row.division || null,
          unit: '',
        },
        projectDrawId: draw.projectDrawSummary.id,
        projectDrawName: payappDrawName || '',
        originalBudget: effectiveOriginalBudget,
        changeOrder: incomingCO,
        reallocation: calculatedReallocation,
        revisedBudget: useRevised ? incomingRevisedBudget : 0,
        previousApplication: parseCurrency(row.previousApplication),
        completeThisPeriod: parseCurrency(row.completeThisPeriod),
        storedThisPeriod: parseCurrency(row.storedThisPeriod),
        quantity: parseFloat(row.quantity) || 0,
        serialNumber: serialNumber++,
        cdccApproved: (parseCurrency(row.completeThisPeriod) || 0) + (parseCurrency(row.storedThisPeriod) || 0),
        completedStoredToDate: 0,
        percentageComplete: 0,
        balanceToComplete: 0,
        retainage: parseCurrency(row.retainage),
        isIncluded: false,
        isCreated: true,
        isEdited: false,
        isDeleted: false,
        labels: row.isChangeOrder ? [LineItemLabelEnum.ChangeOrder] : undefined,
        __payappSourceId: row.__payappSourceId,
      };

      if (bestMatch) {
        // Fuzzy match found
        matchedProjectItemIds.add(bestMatch.item.id);
        const groupId = `fuzzy-group-${payappIndex}`;

        // Prepare raw Payapp values for mismatch comparison
        const payappRowForFuzzy = {
          originalBudget: effectiveOriginalBudget,
          changeOrder: incomingCO,
          reallocation: calculatedReallocation,
          revisedBudget: useRevised ? incomingRevisedBudget : undefined,
          previousApplication: parseCurrency(row.previousApplication),
          completeThisPeriod: parseCurrency(row.completeThisPeriod),
          storedThisPeriod: parseCurrency(row.storedThisPeriod),
          completedStoredToDate: row?.completedStoredToDate
            ? parseCurrency(row.completedStoredToDate)
            : parseCurrency(row.previousApplication) + parseCurrency(row.completeThisPeriod) + parseCurrency(row.storedThisPeriod),
          balanceToComplete: (() => {
            if (row?.balanceToComplete) return parseCurrency(row.balanceToComplete);
            const revised = useRevised ? incomingRevisedBudget : effectiveOriginalBudget + calculatedReallocation + incomingCO;
            const completed =
              (row?.completedStoredToDate
                ? parseCurrency(row.completedStoredToDate)
                : parseCurrency(row.previousApplication) + parseCurrency(row.completeThisPeriod) + parseCurrency(row.storedThisPeriod)) ||
              0;
            return revised - completed;
          })(),
        };

        // Add PayApp item first (with payappRow for UI)
        updatedLineItems.push({
          ...payappLineItem,
          groupId,
          matchType: 'fuzzy',
          fuzzyScore: bestMatch.score,
          budgetMatch: bestMatch.budgetMatch,
          payappRow: payappRowForFuzzy,
          labels: row.isChangeOrder ? [LineItemLabelEnum.ChangeOrder] : payappLineItem.labels,
          // The project row's id is what survives as mergedItem.id once this pair is actually
          // merged (auto or manual) — see autoMergeFuzzyMatch/handleMergeFuzzyMatch, both of which
          // set `id: projectRow.id`. Recording it now lets a subtotal anchor to the correct,
          // eventually-stable row id whether or not the merge has happened yet.
          __fuzzyProjectSideId: bestMatch.item.id,
        });

        // Add Project item immediately after
        updatedLineItems.push({
          ...bestMatch.item,
          previousApplication:
            (bestMatch.item.previousApplication || 0) + (bestMatch.item.completeThisPeriod || 0) + (bestMatch.item.storedThisPeriod || 0),
          completeThisPeriod: 0,
          storedThisPeriod: 0,
          serialNumber: serialNumber++,
          isEdited: true,
          exitingAndNotMatched: true,
          groupId,
          matchType: 'fuzzy',
          fuzzyScore: bestMatch.score,
          budgetMatch: bestMatch.budgetMatch,
        });
      } else {
        // No fuzzy match found - PayApp only
        updatedLineItems.push({
          ...payappLineItem,
          groupId: `payapp-only-${payappIndex}`,
          matchType: 'none',
          // Attach raw Payapp values so we can show them in Preview step
          payappRow: {
            originalBudget: effectiveOriginalBudget,
            changeOrder: incomingCO,
            reallocation: calculatedReallocation,
            revisedBudget: useRevised ? incomingRevisedBudget : undefined,
            previousApplication: parseCurrency(row.previousApplication),
            completeThisPeriod: parseCurrency(row.completeThisPeriod),
            storedThisPeriod: parseCurrency(row.storedThisPeriod),
            completedStoredToDate: row?.completedStoredToDate
              ? parseCurrency(row.completedStoredToDate)
              : parseCurrency(row.previousApplication) + parseCurrency(row.completeThisPeriod) + parseCurrency(row.storedThisPeriod),
            balanceToComplete: (() => {
              if (row?.balanceToComplete) return parseCurrency(row.balanceToComplete);
              const revised = useRevised ? incomingRevisedBudget : effectiveOriginalBudget + calculatedReallocation + incomingCO;
              const completed =
                (row?.completedStoredToDate
                  ? parseCurrency(row.completedStoredToDate)
                  : parseCurrency(row.previousApplication) + parseCurrency(row.completeThisPeriod) + parseCurrency(row.storedThisPeriod)) ||
                0;
              return revised - completed;
            })(),
          },
          labels: row.isChangeOrder ? [LineItemLabelEnum.ChangeOrder] : payappLineItem.labels,
        });
      }
    }
  });

  // Add remaining unmatched project items at the end
  existingLineItems.forEach((projectItem, index) => {
    if (!matchedProjectItemIds.has(projectItem.id)) {
      updatedLineItems.push({
        ...projectItem,
        previousApplication:
          (projectItem.previousApplication || 0) + (projectItem.completeThisPeriod || 0) + (projectItem.storedThisPeriod || 0),
        completeThisPeriod: 0,
        storedThisPeriod: 0,
        serialNumber: serialNumber++,
        isEdited: true,
        exitingAndNotMatched: true,
        groupId: `project-only-${index}`,
        matchType: 'none',
      });
    }
  });

  const existingBudget = draw.budgetSummary || {
    originalContractBudgetSum: 0,
    reallocationsChangeOrders: 0,
    contractBudgetSumToDate: 0,
    totalCompletedAndStored: 0,
    totalRetainageHeld: 0,
    totalRetainageHeldPercentage: 0,
    totalEarnedLessRetainage: 0,
    lessPreviousFundings: 0,
    currentPaymentDue: 0,
    balanceToCompletePlusRetainage: 0,
    adjustmentsByCDCC: 0,
    totalAmountReccForFunding: 0,
    requestedAmount: 0,
    difference: 0,
    estRetainageReleasedThisPeriod: 0,
    totalContractBudgetPerComplete: 0,
  };

  return {
    ...draw,
    projectDrawSummary: {
      ...draw.projectDrawSummary,
      applicationNumber: coverPageValues?.['ApplicationNumber'] ?? 0,
      ...(coverPageValues?.['Period Beginning'] && { periodBeginning: parsePayappDateToISO(String(coverPageValues['Period Beginning'])) }),
      ...(coverPageValues?.['Period Ending'] && { periodEnding: parsePayappDateToISO(String(coverPageValues['Period Ending'])) }),
    },
    lineItems: updatedLineItems,
    budgetSummary: {
      ...existingBudget,
      lessPreviousFundings: Number(coverPageValues?.['LessPreviousFundings'] ?? 0),
      adjustmentsByCDCC: Number(coverPageValues?.['AdjustmentsbyCDCC'] ?? 0),
      requestedAmount: Number(coverPageValues?.['RequestedAmount'] ?? 0),
      estRetainageReleasedThisPeriod: Number(coverPageValues?.['EstRetainageReleasedThisPeriod'] ?? 0),
    },
  } as ProjectLineItemsAndSummary;
};

export const hasPayappMismatch = (row: any): boolean => {
  if (!row.payappRow) return false;

  const fieldsToCompare = [
    'originalBudget',
    'reallocation',
    'changeOrder',
    'revisedBudget',
    'previousApplication',
    'completeThisPeriod',
    'storedThisPeriod',
    'completedStoredToDate',
    'balanceToComplete',
    'retainage',
  ];

  return fieldsToCompare.some(field => {
    const payappValue = row.payappRow[field];
    let rowValue;
    if (field == 'revisedBudget') rowValue = (row.originalBudget || 0) + (row.reallocation || 0) + (row.changeOrder || 0);
    else rowValue = row[field];

    return rowValue !== undefined && payappValue !== undefined && rowValue !== payappValue;
  });
};

export const hasProjectMismatch = (row: any): boolean => {
  if (!row.projectRow) return false;

  const fieldsToCompare = [
    'originalBudget',
    'reallocation',
    'changeOrder',
    'revisedBudget',
    'previousApplication',
    'completeThisPeriod',
    'storedThisPeriod',
    'completedStoredToDate',
    'balanceToComplete',
    'retainage',
  ];

  return fieldsToCompare.some(field => {
    const projectValue = row.projectRow[field];
    let rowValue;
    if (field == 'revisedBudget') rowValue = (row.originalBudget || 0) + (row.reallocation || 0) + (row.changeOrder || 0);
    else rowValue = row[field];

    return rowValue !== undefined && projectValue !== undefined && rowValue !== projectValue;
  });
};

// Mapping for friendly column names
export const friendlyFieldNames: Record<string, string> = {
  originalBudget: 'Original Budget',
  changeOrder: 'Change Order',
  reallocation: 'Reallocation',
  revisedBudget: 'Revised Budget',
  previousApplication: 'Previous Application',
  completeThisPeriod: 'Complete This Period',
  storedThisPeriod: 'Stored This Period',
  cdccApproved: 'CDCC Approved',
  completedStoredToDate: 'Completed Stored To Date',
  percentageComplete: 'Percentage Complete',
  balanceToComplete: 'Balance To Complete',
  retainage: 'Retainage',
  quantity: 'Quantity',
  // Add more mappings if needed
};
// Fuzzy matching configuration
const FUZZY_MATCH_THRESHOLD = 0.6; // Lower is more strict (0.0 = exact match, 1.0 = match anything)
const BUDGET_TOLERANCE_PERCENTAGE = 0.1; // 10% tolerance for budget matching
const LENGTH_SIMILARITY_THRESHOLD = 0.5; // 50% length similarity threshold

/**
 * Performs fuzzy string matching between two descriptions
 */
export const fuzzyMatchDescription = (description1: string, description2: string): number => {
  if (!description1 || !description2) return 0;

  // Normalize both descriptions to handle variations in spacing around punctuation
  // This helps match 'line - item' with 'line-item', 'line/item' with 'line / item', etc.
  const normalized1 = normalizeDescription(description1);
  const normalized2 = normalizeDescription(description2);

  // If normalized strings are identical, return perfect match (1.0) immediately
  if (normalized1 === normalized2) {
    return 1.0;
  }

  // First, check if the descriptions are similar in length
  const len1 = normalized1.length;
  const len2 = normalized2.length;
  const maxLen = Math.max(len1, len2);
  const minLen = Math.min(len1, len2);

  // If one description is significantly shorter than the other, they're unlikely to be a good match
  if (maxLen > 0 && minLen / maxLen < LENGTH_SIMILARITY_THRESHOLD) {
    return 0;
  }

  const fuse = new Fuse([normalized2], {
    includeScore: true,
    threshold: 1.0, // Allow all matches, we'll filter by score
    ignoreLocation: true,
    keys: [''],
  });

  const result = fuse.search(normalized1);

  if (result.length === 0) {
    return 0;
  }

  // Fuse.js returns a score where 0 is perfect match and 1 is no match
  // We invert it to make 1 a perfect match and 0 no match
  // Note: We need to handle the case where score is 0 (perfect match) or undefined/null
  const fuseScore = result[0].score;
  const rawScore = fuseScore !== undefined && fuseScore !== null ? fuseScore : 1;
  const finalScore = 1 - rawScore;

  return finalScore;
};

/**
 * Checks if two budget values are within tolerance
 */
export const budgetWithinTolerance = (budget1: number, budget2: number): boolean => {
  if (budget1 === 0 && budget2 === 0) return true;
  if (budget1 === 0 || budget2 === 0) return false;

  const tolerance = Math.max(budget1, budget2) * BUDGET_TOLERANCE_PERCENTAGE;
  return Math.abs(budget1 - budget2) <= tolerance;
};

// Function to determine if a row should have group borders
export const getRowGroupBorders = (rowIndex: number, rows: ProjectLineItemDTO[]) => {
  const currentRow = rows[rowIndex];
  const prevRow = rowIndex > 0 ? rows[rowIndex - 1] : null;
  const nextRow = rowIndex < rows.length - 1 ? rows[rowIndex + 1] : null;

  if (!currentRow?.groupId || currentRow.matchType !== 'fuzzy') {
    return { isGroupStart: false, isGroupEnd: false, isInGroup: false };
  }

  // For fuzzy matches, check if this row is part of a fuzzy group
  const isInFuzzyGroup = currentRow.groupId && currentRow.matchType === 'fuzzy';

  if (!isInFuzzyGroup) {
    return { isGroupStart: false, isGroupEnd: false, isInGroup: false };
  }

  // Check if previous row is part of the same fuzzy group
  const isPrevInSameGroup = prevRow && prevRow.groupId === currentRow.groupId && prevRow.matchType === 'fuzzy';

  // Check if next row is part of the same fuzzy group
  const isNextInSameGroup = nextRow && nextRow.groupId === currentRow.groupId && nextRow.matchType === 'fuzzy';

  const isGroupStart = !isPrevInSameGroup;
  const isGroupEnd = !isNextInSameGroup;

  return { isGroupStart, isGroupEnd, isInGroup: true };
};

export const stepLabels = {
  VerifyCoverPage: 'Verify Cover Page',
  VerifyTable: 'Verify Table',
  MergeLineItems: 'Merge Into Last Month',
  CompareAgainstLastMonth: 'Review',
} as const;

export const stepTooltips = {
  VerifyCoverPage: 'Review different values extracted from the cover page.',
  VerifyTable: 'Review the line items data extracted from the payapp G702/G703.',
  MergeLineItems: 'Review matched line items and merge payapp data into the project draw.',
  CompareAgainstLastMonth: "Compare the current month's data against the previous month's data.",
} as const;

export const payappProcessTooltips = {
  VerifyCoverPage: (
    <div style={{ color: 'white', width: '300px', textAlign: 'left' }}>
      Verify the values extracted from the cover page in the PDF. <br />
      If the values are not correct, you can edit them.
    </div>
  ),
  VerifyTable: (
    <div style={{ color: 'white', width: '300px', textAlign: 'left' }}>
      <p>Verify the data extracted from the G702/G703 table in the PDF.</p>
      <ul>
        <li>Review the assigned headers and update them if needed.</li>
        <li>If the values are not correct, you can edit them.</li>
        <li>Make sure the totals under each column match the corresponding cover page values.</li>
      </ul>
    </div>
  ),
  CompareAgainstLastMonthBudgetSummary: (
    <div style={{ color: 'white', width: '300px', textAlign: 'left' }}>
      Compare the calculated values against the cover page values.
      <br />
      If there is a mismatch, you can edit the values in the table below to fix it.
    </div>
  ),
  CompareAgainstLastMonthLineItems: (
    <div style={{ color: 'white', whiteSpace: 'pre-wrap', width: '300px', textAlign: 'left' }}>
      <p>
        The system has matched line items between this payapp and last month’s report.
        <br />
      </p>
      <ul>
        <li>Any discrepancies are highlighted in red.</li>
        <li>
          Please double check the system suggested numbers in the top row for every line item. These values will be saved when you hit
          &apos;Confirm&apos;.
        </li>
        <li>Match any unmatched line items to fix the totals. (The system may have grouped possible matches together.)</li>
      </ul>
    </div>
  ),
  AutoDelete: 'Automatically detect rows that are causing totals to mismatch. You can then review and delete them.',
  FixCurrencyFormat: 'The system has detected misplaced commas/decimals in the extracted values. Click here to fix them.',
} as const;

// Normalize description strings for comparison (handles spaces around punctuation)
export const normalizeDescription = (str: string): string => {
  return str
    .trim() // Remove hanging spaces
    .replace(/\s*\/\s*/g, '/') // Normalize '/' format variations
    .replace(/\s*-\s*/g, '-') // Normalize '-' format variations
    .replace(/\s*,\s*/g, ',') // Normalize ',' format variations
    .toLowerCase(); // Case-insensitive: "XYZ" = "xyz" = "XyZ"
};

// Checks if two descriptions should be automatically merged
export const shouldAutoMergeDescriptions = (desc1: string, desc2: string): boolean => {
  if (!desc1 || !desc2) return false;
  return normalizeDescription(desc1) === normalizeDescription(desc2);
};

/**
 * Automatically merges fuzzy-matched line items without user confirmation
 * This is used when descriptions match specific criteria (hanging spaces, '/' and '-' format variations)
 */
export const autoMergeFuzzyMatch = (
  payappRow: ProjectLineItemDTO,
  projectRow: ProjectLineItemDTO,
  lineItems: ProjectLineItemDTO[],
  /** Set of column header names assigned in step 2; when a column is not in the set, we use project value and omit from comparison */
  assignedPayappColumns?: AssignedPayappColumns,
): ProjectLineItemDTO[] => {
  const hasOriginalBudgetColumn = hasPayappColumn(assignedPayappColumns, 'Original Budget');
  const hasChangeOrderColumn = hasPayappColumn(assignedPayappColumns, 'Change Order');
  const hasReallocationColumn = hasPayappColumn(assignedPayappColumns, 'Reallocation');
  // Create PayApp line item representation
  const revisedBudgetRaw = payappRow.revisedBudget;
  const useRevised = revisedBudgetRaw !== undefined && revisedBudgetRaw !== null && revisedBudgetRaw.toString() !== '';
  const incomingRevisedBudget = useRevised ? parseCurrency(revisedBudgetRaw) : 0;

  const changeOrderValue =
    hasChangeOrderColumn === true &&
    payappRow.changeOrder !== undefined &&
    payappRow.changeOrder !== null &&
    payappRow?.changeOrder.toString() !== ''
      ? parseCurrency(payappRow.changeOrder)
      : (projectRow.changeOrder ?? 0);

  const calculatedReallocation = useRevised
    ? incomingRevisedBudget - (projectRow.originalBudget + changeOrderValue)
    : parseCurrency(payappRow.reallocation);

  // Create the merged line item
  const mergedItem: ProjectLineItemDTO = {
    ...payappRow,
    originalBudget: projectRow.originalBudget,
    previousApplication: (projectRow.previousApplication || 0) + (projectRow.completeThisPeriod || 0) + (projectRow.storedThisPeriod || 0),
    completeThisPeriod: parseCurrency(payappRow.completeThisPeriod),
    storedThisPeriod: parseCurrency(payappRow.storedThisPeriod),
    cdccApproved: (parseCurrency(payappRow.completeThisPeriod) || 0) + (parseCurrency(payappRow.storedThisPeriod) || 0),
    changeOrder: changeOrderValue,
    reallocation: calculatedReallocation,
    retainage:
      payappRow.retainage !== undefined && payappRow.retainage !== null && payappRow.retainage.toString() !== ''
        ? parseCurrency(payappRow.retainage)
        : (projectRow.retainage ?? 0),
    id: projectRow.id, // Keep the project row's ID
    lineItem: {
      ...projectRow.lineItem,
      description: payappRow.lineItem.description, // Keep Payapp description
      division: payappRow.lineItem.division || projectRow.lineItem.division, // Prefer PayApp division
    },
    isEdited: true,
    isCreated: false,
    exitingAndNotMatched: false,
    matchType: 'exact', // Change to exact since we're merging
    groupId: undefined, // Remove group ID
    // Store original fuzzy match info for undo
    fuzzyScore: payappRow.fuzzyScore,
    budgetMatch: payappRow.budgetMatch,
    // Keep both payappRow and projectRow for comparison and undo
    payappRow: {
      ...payappRow,
      // The payapp line item holds placeholder 0s for these; the raw payapp values live in its nested payappRow
      completedStoredToDate: payappRow.payappRow?.completedStoredToDate ?? payappRow.completedStoredToDate,
      balanceToComplete: payappRow.payappRow?.balanceToComplete ?? payappRow.balanceToComplete,
      ...(hasOriginalBudgetColumn === false && { originalBudget: undefined }),
      ...(hasChangeOrderColumn === false && { changeOrder: undefined }),
      ...(hasReallocationColumn === false && { reallocation: undefined }),
      ...(!useRevised && { revisedBudget: undefined }),
    },
    projectRow: {
      ...projectRow.projectRow,
      // Store original project item info for undo (including lineItem)
      id: projectRow.id,
      groupId: projectRow.groupId,
      lineItem: projectRow.lineItem,
      originalBudget: projectRow.originalBudget,
      reallocation: hasReallocationColumn ? projectRow.reallocation : undefined,
      changeOrder: projectRow.changeOrder,
      revisedBudget: calculateRevisedBudget(projectRow),
      previousApplication: projectRow.previousApplication,
      completeThisPeriod: projectRow.completeThisPeriod,
      storedThisPeriod: projectRow.storedThisPeriod,
      completedStoredToDate: calculateTotalCompletedStored(projectRow),
      balanceToComplete: calculateBalanceToComplete(projectRow),
      retainage: projectRow.retainage,
    },
  };

  // Function to remove grouping from orphaned items
  const removeGroupingFromItem = (item: ProjectLineItemDTO): ProjectLineItemDTO => {
    return {
      ...item,
      groupId: undefined,
      matchType: 'none' as const,
    };
  };

  // Update line items
  const updatedLineItems = lineItems.map(item => {
    // Handle the merged items
    if (item.id === projectRow?.id) {
      return mergedItem;
    } else if (item.id === payappRow.id) {
      return { ...item, isDeleted: true, isCreated: false }; // Mark PayApp item as deleted
    }
    // Clean up any orphaned fuzzy items from both groups involved in the merge
    const isOrphanedFromPayappGroup =
      payappRow.groupId &&
      item.groupId === payappRow.groupId &&
      item.matchType === 'fuzzy' &&
      item.id !== projectRow?.id &&
      item.id !== payappRow.id;

    const isOrphanedFromProjectGroup =
      projectRow?.groupId &&
      item.groupId === projectRow.groupId &&
      item.matchType === 'fuzzy' &&
      item.id !== payappRow.id &&
      item.id !== projectRow?.id;

    if (isOrphanedFromPayappGroup || isOrphanedFromProjectGroup) {
      return removeGroupingFromItem(item);
    }

    return item;
  });

  // Swap merged project item to deleted payapp position
  const projectIndex = updatedLineItems.findIndex(i => i.id === projectRow?.id);
  const payappIndex = updatedLineItems.findIndex(i => i.id === payappRow.id);

  if (projectIndex !== -1 && payappIndex !== -1) {
    const [projectItem] = updatedLineItems.splice(projectIndex, 1);
    updatedLineItems.splice(payappIndex, 0, projectItem);
  }

  return updatedLineItems;
};

/**
 * Automatically merges all fuzzy-matched line items that meet auto-merge criteria
 * This should be called after mergePayappIntoProjectDraw creates fuzzy matches
 */
export const autoMergeFuzzyMatches = (
  projectDraw: ProjectLineItemsAndSummary,
  /** Set of column header names assigned in step 2 */
  assignedPayappColumns?: AssignedPayappColumns,
): ProjectLineItemsAndSummary => {
  if (!projectDraw.lineItems) return projectDraw;

  let lineItems = [...projectDraw.lineItems];
  const processedGroupIds = new Set<string>();

  // Collect all pairs that should be auto-merged (store IDs to avoid stale references)
  const mergesToPerform: Array<{ payappItemId: string; projectItemId: string }> = [];

  // First pass: identify all pairs that should be auto-merged
  for (const payappItem of lineItems) {
    // Skip if already processed or not a fuzzy match
    if (!payappItem.groupId || payappItem.matchType !== 'fuzzy' || !payappItem.isCreated || processedGroupIds.has(payappItem.groupId)) {
      continue;
    }

    // Find the corresponding project item in the same group
    const projectItem = lineItems.find(
      item => item.groupId === payappItem.groupId && item.exitingAndNotMatched && item.matchType === 'fuzzy',
    );

    if (!projectItem) continue;

    // Check if descriptions should be auto-merged
    const payappDescription = payappItem.lineItem.description || '';
    const projectDescription = projectItem.lineItem.description || '';

    if (shouldAutoMergeDescriptions(payappDescription, projectDescription)) {
      mergesToPerform.push({ payappItemId: payappItem.id, projectItemId: projectItem.id });
      processedGroupIds.add(payappItem.groupId);
    }
  }

  // Second pass: perform all merges by finding items by ID each time
  for (const { payappItemId, projectItemId } of mergesToPerform) {
    const payappItem = lineItems.find(item => item.id === payappItemId);
    const projectItem = lineItems.find(item => item.id === projectItemId);

    if (payappItem && projectItem) {
      lineItems = autoMergeFuzzyMatch(payappItem, projectItem, lineItems, assignedPayappColumns);
    }
  }

  return {
    ...projectDraw,
    lineItems,
  };
};

export const handleMergeFuzzyMatch = async (
  payappRow: ProjectLineItemDTO,
  filteredLineItems: ProjectLineItemDTO[],
  projectDraw: ProjectLineItemsAndSummary,
  setProjectDraw: React.Dispatch<React.SetStateAction<ProjectLineItemsAndSummary>>,
  getConfirmation?: (params: any) => Promise<boolean>,
  projectRowId?: string,
  /** Set of column header names assigned in step 2; when a column is not in the set, we use project value and omit from comparison */
  assignedPayappColumns?: AssignedPayappColumns,
) => {
  const hasOriginalBudgetColumn = hasPayappColumn(assignedPayappColumns, 'Original Budget');
  const hasChangeOrderColumn = hasPayappColumn(assignedPayappColumns, 'Change Order');
  const hasReallocationColumn = hasPayappColumn(assignedPayappColumns, 'Reallocation');
  // Find the project row that's part of this fuzzy match group
  let projectRow: ProjectLineItemDTO | undefined;

  if (projectRowId) {
    projectRow = filteredLineItems.find(item => item.id === projectRowId);
  } else {
    projectRow = filteredLineItems.find(item => item.groupId === payappRow.groupId && item.exitingAndNotMatched);
  }

  if (!projectRow) {
    toast.error('Could not find matching last month line item to merge');
    return;
  }

  try {
    // Only show confirmation if getConfirmation is provided
    if (getConfirmation) {
      const isConfirmed = await getConfirmation({
        heading: 'Merge Line Items',
        description: 'Are you sure you want to merge these line items? This will combine them into a single line item.',
        confirmButton: {
          label: 'Merge',
          color: 'primary',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;
      await new Promise(resolve => setTimeout(resolve, 0));
    }

    // Create PayApp line item representation
    const revisedBudgetRaw = payappRow.revisedBudget;
    const useRevised = revisedBudgetRaw !== undefined && revisedBudgetRaw !== null && revisedBudgetRaw.toString() !== '';
    const incomingRevisedBudget = useRevised ? parseCurrency(revisedBudgetRaw) : 0;

    const changeOrderValue =
      hasChangeOrderColumn === true &&
      payappRow.changeOrder !== undefined &&
      payappRow.changeOrder !== null &&
      payappRow?.changeOrder.toString() !== ''
        ? parseCurrency(payappRow.changeOrder)
        : (projectRow.changeOrder ?? 0);

    const calculatedReallocation = useRevised
      ? incomingRevisedBudget - (projectRow.originalBudget + changeOrderValue)
      : parseCurrency(payappRow.reallocation);
    // Create the merged line item
    // Store original payapp item ID and groupId for undo functionality
    const mergedItem: ProjectLineItemDTO = {
      ...payappRow,
      description: payappRow.lineItem.description, // Keep Payapp description
      originalBudget: projectRow.originalBudget,
      previousApplication:
        (projectRow.previousApplication || 0) + (projectRow.completeThisPeriod || 0) + (projectRow.storedThisPeriod || 0),
      completeThisPeriod: parseCurrency(payappRow.completeThisPeriod),
      storedThisPeriod: parseCurrency(payappRow.storedThisPeriod),
      cdccApproved: (parseCurrency(payappRow.completeThisPeriod) || 0) + (parseCurrency(payappRow.storedThisPeriod) || 0),
      changeOrder: changeOrderValue,
      reallocation: calculatedReallocation,
      // quantity: parseFloat(payappRow.quantity) || 0,
      retainage:
        payappRow.retainage !== undefined && payappRow.retainage !== null && payappRow.retainage.toString() !== ''
          ? parseCurrency(payappRow.retainage)
          : (projectRow.retainage ?? 0),
      id: projectRow.id, // Keep the project row's ID
      lineItem: {
        ...projectRow.lineItem,
        description: payappRow.lineItem.description, // Keep Payapp description
        division: payappRow.lineItem.division || projectRow.lineItem.division, // Prefer PayApp division
      },
      isEdited: true,
      isCreated: false,
      exitingAndNotMatched: false,
      matchType: 'exact', // Change to exact since we're merging
      groupId: undefined, // Remove group ID
      // Store original fuzzy match info for undo
      fuzzyScore: payappRow.fuzzyScore,
      budgetMatch: payappRow.budgetMatch,
      // Keep both payappRow and projectRow for comparison and undo
      payappRow: {
        ...payappRow,
        // The payapp line item holds placeholder 0s for these; the raw payapp values live in its nested payappRow
        completedStoredToDate: payappRow.payappRow?.completedStoredToDate ?? payappRow.completedStoredToDate,
        balanceToComplete: payappRow.payappRow?.balanceToComplete ?? payappRow.balanceToComplete,
        // Only clear columns that were explicitly NOT assigned in step 2
        ...(hasOriginalBudgetColumn === false && { originalBudget: undefined }),
        ...(hasChangeOrderColumn === false && { changeOrder: undefined }),
        ...(hasReallocationColumn === false && { reallocation: undefined }),
        ...(!useRevised && { revisedBudget: undefined }),
      },
      projectRow: {
        ...projectRow.projectRow,
        // Store original project item info for undo (including lineItem)
        id: projectRow.id,
        groupId: projectRow.groupId,
        lineItem: projectRow.lineItem,
        originalBudget: projectRow.originalBudget,
        reallocation: hasReallocationColumn ? projectRow.reallocation : undefined,
        changeOrder: projectRow.changeOrder,
        revisedBudget: calculateRevisedBudget(projectRow),
        previousApplication: projectRow.previousApplication,
        completeThisPeriod: projectRow.completeThisPeriod,
        storedThisPeriod: projectRow.storedThisPeriod,
        completedStoredToDate: calculateTotalCompletedStored(projectRow),
        balanceToComplete: calculateBalanceToComplete(projectRow),
        retainage: projectRow.retainage,
      },
    };
    // Function to update an item's fuzzy match properties
    // Function to update an item's fuzzy match properties
    const removeGroupingFromItem = (item: ProjectLineItemDTO): ProjectLineItemDTO => {
      return {
        ...item,
        groupId: undefined,
        matchType: 'none' as const,
      };
    };

    // Update both projectDraw.lineItems and filteredLineItems
    const updatedLineItems = projectDraw.lineItems?.map(item => {
      // Handle the merged items
      if (item.id === projectRow?.id) {
        return mergedItem;
      } else if (item.id === payappRow.id) {
        return { ...item, isDeleted: true, isCreated: false }; // Mark PayApp item as deleted
      }
      // Clean up any orphaned fuzzy items from both groups involved in the merge
      // This handles both automatic fuzzy matches and manual merges
      const isOrphanedFromPayappGroup =
        payappRow.groupId &&
        item.groupId === payappRow.groupId &&
        item.matchType === 'fuzzy' &&
        item.id !== projectRow?.id &&
        item.id !== payappRow.id;

      const isOrphanedFromProjectGroup =
        projectRow?.groupId &&
        item.groupId === projectRow.groupId &&
        item.matchType === 'fuzzy' &&
        item.id !== payappRow.id &&
        item.id !== projectRow?.id;

      if (isOrphanedFromPayappGroup || isOrphanedFromProjectGroup) {
        return removeGroupingFromItem(item);
      }

      return item;
    });

    // Swap mergedProject item to deleted payapp position
    if (updatedLineItems) {
      const projectIndex = updatedLineItems.findIndex(i => i.id === projectRow?.id);
      const payappIndex = updatedLineItems.findIndex(i => i.id === payappRow.id);

      if (projectIndex !== -1 && payappIndex !== -1) {
        const [projectItem] = updatedLineItems.splice(projectIndex, 1);
        updatedLineItems.splice(payappIndex, 0, projectItem);
      }
    }
    setProjectDraw({
      ...projectDraw,
      lineItems: updatedLineItems,
    });

    // Success feedback removed to avoid lag when merging multiple items in sequence
  } catch (error) {
    console.error('Error merging line items:', error);
    toast.error('An error occurred while merging the line items');
  }
};

/**
 * Undoes a merge by splitting a merged line item back into fuzzy-matched payapp and project items
 */
export const undoMergeFuzzyMatch = (
  mergedItem: ProjectLineItemDTO,
  projectDraw: ProjectLineItemsAndSummary,
  setProjectDraw: React.Dispatch<React.SetStateAction<ProjectLineItemsAndSummary>>,
) => {
  if (!mergedItem.payappRow || !mergedItem.projectRow) {
    toast.error('Cannot undo merge: original item data not found');
    return;
  }

  // Extract original IDs and groupId from stored data
  const originalPayappId = (mergedItem.payappRow as any).id;
  const originalProjectId = (mergedItem.projectRow as any).id;
  const originalGroupId = (mergedItem.payappRow as any).groupId || (mergedItem.projectRow as any).groupId;

  if (!originalPayappId || !originalProjectId) {
    toast.error('Cannot undo merge: original item IDs not found');
    return;
  }

  try {
    // Get the original payapp line item description - need to find it in deleted items or use stored data
    const deletedPayappItem = projectDraw.lineItems?.find(item => item.id === originalPayappId && item.isDeleted);
    const payappDescription =
      deletedPayappItem?.lineItem.description || (mergedItem.payappRow as any).lineItem?.description || mergedItem.lineItem.description;
    const payappDivision =
      deletedPayappItem?.lineItem.division || (mergedItem.payappRow as any).lineItem?.division || mergedItem.lineItem.division;

    // Restore the payapp item — spread the full snapshot stored at merge time, then override identity/state fields.
    // Budget fields (OB, reallocation, CO, revisedBudget) are restored from the deleted item preserved in the list
    // because payappRow may have some of them nulled for mismatch-display purposes.
    const restoredPayappItem: ProjectLineItemDTO = {
      ...(mergedItem.payappRow as unknown as ProjectLineItemDTO),
      // Restore pre-merge budget fields from the preserved deleted payapp item so that the
      // computed Revised Budget display (OB + Realloc + CO) renders correctly after undo.
      ...(deletedPayappItem && {
        originalBudget: deletedPayappItem.originalBudget,
        reallocation: deletedPayappItem.reallocation,
        changeOrder: deletedPayappItem.changeOrder,
        revisedBudget: deletedPayappItem.revisedBudget,
      }),
      id: originalPayappId,
      lineItem: {
        id: '',
        description: payappDescription || '',
        division: payappDivision || null,
        unit: '',
      },
      projectDrawId: mergedItem.projectDrawId,
      projectDrawName: mergedItem.projectDrawName,
      quantity: deletedPayappItem?.quantity ?? (mergedItem.payappRow as any).quantity ?? 0,
      serialNumber: mergedItem.serialNumber || 0,
      cdccApproved: (mergedItem.payappRow.completeThisPeriod ?? 0) + (mergedItem.payappRow.storedThisPeriod ?? 0),
      isIncluded: false,
      isCreated: true,
      isEdited: false,
      isDeleted: false,
      matchType: 'fuzzy',
      groupId: originalGroupId,
      fuzzyScore: mergedItem.fuzzyScore,
      budgetMatch: mergedItem.budgetMatch,
      labels: deletedPayappItem?.labels ?? (mergedItem.payappRow as any).labels,
    };

    // Restore the project item
    const restoredProjectItem: ProjectLineItemDTO = {
      id: originalProjectId,
      lineItem: {
        id: (mergedItem.projectRow as any).lineItem?.id || mergedItem.lineItem.id || '',
        description: (mergedItem.projectRow as any).lineItem?.description || mergedItem.lineItem.description,
        division: (mergedItem.projectRow as any).lineItem?.division || mergedItem.lineItem.division,
        unit: '',
      },
      projectDrawId: mergedItem.projectDrawId,
      projectDrawName: mergedItem.projectDrawName,
      originalBudget: mergedItem.projectRow.originalBudget ?? 0,
      reallocation: mergedItem.projectRow.reallocation ?? 0,
      changeOrder: mergedItem.projectRow.changeOrder ?? 0,
      revisedBudget: mergedItem.projectRow.revisedBudget ?? 0,
      previousApplication: mergedItem.projectRow.previousApplication ?? 0,
      completeThisPeriod: 0,
      storedThisPeriod: 0,
      quantity: 0,
      serialNumber: (mergedItem.serialNumber || 0) + 1,
      cdccApproved: 0,
      completedStoredToDate: mergedItem.projectRow.completedStoredToDate ?? 0,
      percentageComplete: 0,
      balanceToComplete: mergedItem.projectRow.balanceToComplete ?? 0,
      retainage: mergedItem.projectRow.retainage ?? 0,
      isIncluded: false,
      isCreated: false,
      isEdited: true,
      exitingAndNotMatched: true,
      matchType: 'fuzzy',
      groupId: originalGroupId,
      fuzzyScore: mergedItem.fuzzyScore,
      budgetMatch: mergedItem.budgetMatch,
    };

    // Update line items: replace merged item with restored payapp and project items, and restore deleted payapp item
    const updatedLineItems =
      projectDraw.lineItems
        ?.filter(item => item.id !== mergedItem.id && !(item.id === originalPayappId && item.isDeleted))
        .map(item => item) || [];

    // Find the position where merged item was (or use its serial number to determine position)
    const mergedIndex = projectDraw.lineItems?.findIndex(item => item.id === mergedItem.id) ?? -1;

    if (mergedIndex !== -1) {
      // Insert payapp item at the merged item's position, then project item right after
      updatedLineItems.splice(mergedIndex, 0, restoredPayappItem, restoredProjectItem);
    } else {
      // Fallback: add both items at the end
      updatedLineItems.push(restoredPayappItem, restoredProjectItem);
    }

    setProjectDraw({
      ...projectDraw,
      lineItems: updatedLineItems,
    });

    toast.success('Merge undone successfully');
  } catch (error) {
    console.error('Error undoing merge:', error);
    toast.error('An error occurred while undoing the merge');
  }
};

/**
 * Detects whether the current payapp represents a skipped period.
 * All three conditions must be true:
 *  1. The application number advanced by more than 1 from the project's last recorded draw.
 *  2. The payapp's Previous Application sum differs from the project's.
 *  3. The project's Less Previous Fundings value is lower than the payapp's cover page value.
 */
export const detectSkippedPayapp = (
  mergedDraw: ProjectLineItemsAndSummary,
  originalProjectDraw: ProjectLineItemsAndSummary,
  coverPageValues: CoverPageValues,
): { isSkipped: boolean; skippedRange: { start: number; end: number } | null } => {
  const currentAppNum = parseInt((coverPageValues['Application Number'] ?? '0').toString()) || 0;
  const previousAppNum = parseInt(originalProjectDraw.projectDrawSummary?.applicationNumber || '0') || 0;

  // Condition 1: payapp number advanced by more than 1
  const advancedByMoreThanOne = currentAppNum - previousAppNum > 1;

  // Condition 2: Previous Application sum mismatch between payapp rows and project rows
  const payappPrevSum =
    mergedDraw.lineItems
      ?.filter(item => !item.isDeleted && item.payappRow)
      .reduce((s, item) => s + ((item.payappRow as any)?.previousApplication || 0), 0) ?? 0;
  const projectPrevSum = (originalProjectDraw.lineItems ?? [])
    .filter(item => !item.isDeleted)
    .reduce((s, item) => s + (item.previousApplication || 0), 0);
  const prevAppMismatch = Math.abs(payappPrevSum - projectPrevSum) > 0.01;

  // Condition 3: Project LPF < Payapp LPF
  const projectLPF = originalProjectDraw.budgetSummary?.lessPreviousFundings ?? 0;
  const payappLPF = parseCurrency(normalizeValue((coverPageValues['Less Previous Fundings'] ?? '0').toString()) || '0');
  const projectLPFLower = projectLPF < payappLPF;

  const isSkipped = advancedByMoreThanOne && prevAppMismatch && projectLPFLower;
  const skippedRange = isSkipped ? { start: previousAppNum + 1, end: currentAppNum - 1 } : null;

  return { isSkipped, skippedRange };
};

/**
 * Re-runs description-based fuzzy matching on all currently unresolved rows
 * (matchType 'fuzzy' or 'none'). Already-merged rows (matchType 'exact') are left untouched.
 */
export const rematchByDescription = (projectDraw: ProjectLineItemsAndSummary): ProjectLineItemsAndSummary => {
  if (!projectDraw.lineItems) return projectDraw;

  const unresolved = projectDraw.lineItems.filter(item => item.matchType !== 'exact');

  // Restore payapp-only and project-only rows to clean 'none' state, discarding any existing fuzzy groupings
  const payappRows = unresolved
    .filter(item => item.isCreated && !item.exitingAndNotMatched)
    .map(item => ({
      ...item,
      matchType: 'none' as const,
      groupId: item.groupId?.startsWith('payapp-only') ? item.groupId : `payapp-only-${item.id}`,
      fuzzyScore: undefined,
      budgetMatch: undefined,
    }));

  const projectRows = unresolved
    .filter(item => item.exitingAndNotMatched)
    .map(item => ({
      ...item,
      matchType: 'none' as const,
      groupId: item.groupId?.startsWith('project-only') ? item.groupId : `project-only-${item.id}`,
      fuzzyScore: undefined,
      budgetMatch: undefined,
    }));

  const matchedProjectIds = new Set<string>();
  const result: ProjectLineItemDTO[] = [];
  let groupCounter = 0;

  for (const payappItem of payappRows) {
    const payappDesc = (payappItem.lineItem.description || '').trim().toLowerCase();

    const potentialMatches: Array<{ item: ProjectLineItemDTO; score: number; budgetMatch: boolean }> = [];
    for (const projectItem of projectRows) {
      if (matchedProjectIds.has(projectItem.id)) continue;
      const projectDesc = (projectItem.lineItem.description || '').trim().toLowerCase();
      const score = fuzzyMatchDescription(payappDesc, projectDesc);
      const payappRevised = (payappItem.originalBudget || 0) + (payappItem.reallocation || 0) + (payappItem.changeOrder || 0);
      const projectRevised = (projectItem.originalBudget || 0) + (projectItem.reallocation || 0) + (projectItem.changeOrder || 0);
      if (score >= FUZZY_MATCH_THRESHOLD) {
        potentialMatches.push({ item: projectItem, score, budgetMatch: budgetWithinTolerance(payappRevised, projectRevised) });
      }
    }

    potentialMatches.sort((a, b) => {
      if (a.budgetMatch && !b.budgetMatch) return -1;
      if (!a.budgetMatch && b.budgetMatch) return 1;
      return b.score - a.score;
    });

    const best = potentialMatches[0];
    if (best) {
      matchedProjectIds.add(best.item.id);
      const groupId = `fuzzy-group-${groupCounter++}`;
      result.push({ ...payappItem, groupId, matchType: 'fuzzy', fuzzyScore: best.score, budgetMatch: best.budgetMatch });
      result.push({ ...best.item, groupId, matchType: 'fuzzy', fuzzyScore: best.score, budgetMatch: best.budgetMatch });
    } else {
      result.push(payappItem);
    }
  }

  // Add unmatched project rows
  for (const projectItem of projectRows) {
    if (!matchedProjectIds.has(projectItem.id)) {
      result.push(projectItem);
    }
  }

  // Rebuild preserving original positions of frozen rows; unresolved slots are filled with new result in order.
  // Non-exact slots in the original order that have no corresponding result entry are dropped intentionally —
  // this handles the case where rematch produces fewer rows than before (e.g. pairs collapsed into one).
  // Any surplus result rows are appended at the end.
  const merged: ProjectLineItemDTO[] = [];
  let resultIdx = 0;
  for (const item of projectDraw.lineItems) {
    if (item.matchType === 'exact') {
      merged.push(item);
    } else if (resultIdx < result.length) {
      merged.push(result[resultIdx++]);
    }
    // else: original non-exact slot has no result counterpart — row is intentionally omitted
  }
  while (resultIdx < result.length) merged.push(result[resultIdx++]);

  return { ...projectDraw, lineItems: merged };
};

/**
 * Matches unresolved payapp-only and project-only rows by revised budget (exact equality).
 * Only pairs where exactly one payapp row and one project row share the same revised budget are matched.
 * Payapp rows without a revisedBudget column assigned are skipped entirely.
 * Already-merged rows (matchType 'exact') are left untouched.
 */
export const rematchByBudget = (
  projectDraw: ProjectLineItemsAndSummary,
  assignedPayappColumns?: AssignedPayappColumns,
): ProjectLineItemsAndSummary => {
  if (!projectDraw.lineItems) return projectDraw;

  const hasRevisedBudgetColumn = hasPayappColumn(assignedPayappColumns, 'Revised Budget');
  if (!hasRevisedBudgetColumn) {
    toast.info('Match by Budget requires a Revised Budget column to be assigned in step 2.');
    return projectDraw;
  }

  const unresolved = projectDraw.lineItems.filter(item => item.matchType !== 'exact');

  const payappRows = unresolved
    .filter(item => item.isCreated && !item.exitingAndNotMatched)
    .map(item => ({
      ...item,
      matchType: 'none' as const,
      groupId: `payapp-only-${item.id}`,
      fuzzyScore: undefined,
      budgetMatch: undefined,
    }));

  const projectRows = unresolved
    .filter(item => item.exitingAndNotMatched)
    .map(item => ({
      ...item,
      matchType: 'none' as const,
      groupId: `project-only-${item.id}`,
      fuzzyScore: undefined,
      budgetMatch: undefined,
    }));

  // For payapp rows: prefer the revisedBudget field (directly from the payapp document, survives restore after unmerge).
  // Fall back to computed value in case revisedBudget wasn't assigned as a column.
  // For project rows: always compute from components since they have no payapp-sourced revisedBudget.
  const getPayappRevisedBudget = (item: ProjectLineItemDTO) =>
    item.revisedBudget ?? (item.originalBudget || 0) + (item.reallocation || 0) + (item.changeOrder || 0);

  const getProjectRevisedBudget = (item: ProjectLineItemDTO) =>
    (item.originalBudget || 0) + (item.reallocation || 0) + (item.changeOrder || 0);

  const payappByBudget = new Map<number, ProjectLineItemDTO[]>();
  for (const item of payappRows) {
    const revised = getPayappRevisedBudget(item);
    if (revised === null || revised === undefined) continue;
    const key = Math.round(revised * 100);
    if (!payappByBudget.has(key)) payappByBudget.set(key, []);
    payappByBudget.get(key)!.push(item);
  }

  const projectByBudget = new Map<number, ProjectLineItemDTO[]>();
  for (const item of projectRows) {
    const revised = getProjectRevisedBudget(item);
    if (revised === null || revised === undefined) continue;
    const key = Math.round(revised * 100);
    if (!projectByBudget.has(key)) projectByBudget.set(key, []);
    projectByBudget.get(key)!.push(item);
  }

  const matchedPayappIds = new Set<string>();
  const matchedProjectIds = new Set<string>();
  const pairs: Array<{ payapp: ProjectLineItemDTO; project: ProjectLineItemDTO }> = [];

  for (const [key, payapps] of Array.from(payappByBudget)) {
    const projects = projectByBudget.get(key) || [];
    // Only match when exactly 1-to-1
    if (payapps.length === 1 && projects.length === 1) {
      matchedPayappIds.add(payapps[0].id);
      matchedProjectIds.add(projects[0].id);
      pairs.push({ payapp: payapps[0], project: projects[0] });
    }
  }

  const result: ProjectLineItemDTO[] = [];
  pairs.forEach(({ payapp, project }, i) => {
    const groupId = `fuzzy-group-budget-${i}`;
    result.push({ ...payapp, groupId, matchType: 'fuzzy', budgetMatch: true, fuzzyScore: 1 });
    result.push({ ...project, groupId, matchType: 'fuzzy', budgetMatch: true, fuzzyScore: 1 });
  });

  for (const item of payappRows) {
    if (!matchedPayappIds.has(item.id)) result.push(item);
  }
  for (const item of projectRows) {
    if (!matchedProjectIds.has(item.id)) result.push(item);
  }

  const merged: ProjectLineItemDTO[] = [];
  let resultIdx = 0;
  for (const item of projectDraw.lineItems) {
    if (item.matchType === 'exact') {
      merged.push(item);
    } else if (resultIdx < result.length) {
      merged.push(result[resultIdx++]);
    }
    // else: original non-exact slot has no result counterpart — row is intentionally omitted
  }
  while (resultIdx < result.length) merged.push(result[resultIdx++]);

  return { ...projectDraw, lineItems: merged };
};
